import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '../ui/skeleton';
import EntryOptionsSummary from '../trophy/EntryOptionsSummary';
import { useEntryOptions } from '../trophy/useEntryOptions';
import { isUnderConsentAge } from '../../utils/consent-age';
import type { EntryOptions } from '../../types/trophy';
import type { AthleteDraft } from './athlete-draft';

/** Where one child's preview stands, as the page needs it to know who is ready. */
export interface AthleteEntryStatus {
  options: EntryOptions | null;
  loading: boolean;
}

interface AthleteEntryOptionsProps {
  stageSlug: string;
  athlete: AthleteDraft;
  /** The turn picked for this child. */
  onRoundChange: (roundId: string) => void;
  /** Told whenever the preview changes, so the page can hold the submit back. */
  onStatus: (key: string, status: AthleteEntryStatus) => void;
}

/**
 * One child's class and rounds at the stage the parent is signing up for —
 * what the athlete's own entry dialog shows, read from the public
 * `entry_options` since nobody is signed in yet — and the turn to book when
 * the class is split over two or more.
 *
 * Asked once the child's date of birth (under 14: there is nobody to hold an
 * account for otherwise) and gender are in, and again as either changes.
 */
export default function AthleteEntryOptions({ stageSlug, athlete, onRoundChange, onStatus }: AthleteEntryOptionsProps) {
  const { t } = useTranslation();
  const ready = !!athlete.gender && athlete.birth_date.length === 10 && isUnderConsentAge(athlete.birth_date);
  const { options, loading, error } = useEntryOptions(
    stageSlug,
    ready ? { birthDate: athlete.birth_date, gender: athlete.gender ?? null } : null
  );

  // The page's handler may be a new function on every render: the latest one
  // is used, and only a change in the preview itself tells the page again.
  const report = useRef(onStatus);
  useEffect(() => {
    report.current = onStatus;
  });
  useEffect(() => {
    report.current(athlete.key, { options, loading });
  }, [athlete.key, options, loading]);

  return (
    <div className="min-w-0 space-y-2 border-t pt-3" data-testid="athlete-entry-options">
      {options ? (
        <EntryOptionsSummary
          options={options}
          roundId={athlete.competition_round_id ?? null}
          onRoundChange={onRoundChange}
          name={`entry-turn-${athlete.key}`}
        />
      ) : loading ? (
        <div className="space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid="athlete-entry-options-pending">
          {t('guardian.signUp.optionsPending')}
        </p>
      )}
      {error !== null && (
        <p className="text-xs text-destructive">{error || t('guardian.signUp.optionsError')}</p>
      )}
    </div>
  );
}
